// swimlaneFrames — the frame / subprocess overlay renderers for the swimlane
// board: the `do` disclosure band, the flow-variant subprocess + `each`-loop
// brackets, the sequence subprocess section box, and the frame group (rect +
// band dividers + branch-pick tabs). Pure presentational components — geometry
// comes from the committed RowGeometry + solved frame rects; no layout math
// beyond trivial row-span sums.

import type { Row, Frame, FrameBranch, NodeId, SwimlaneLayout } from "@archlang/engine";
import {
  LANE_WIDTH,
  LANE_HEADER_H,
  ROW_MIN_H,
  BLOCK_INSET,
  NESTED_INSET_X,
  NESTED_INSET_Y,
  FRAME_GAP,
  type SwimlaneBlockGeom,
} from "@archlang/scene/process";
import { svgFragmentUrl } from "../svgFragmentUrl";
import { isFrameExclusive, showsBranchLabels } from "./frameAffordance";
import type { RowGeometry } from "./swimlaneRows.js";

const SUBPROCESS_BORDER = "#6366f1";

/**
 * Disclosure header for a subprocess `do` (caret + label, click toggles).
 *
 * - Sequence: a full-width band spanning every lane. When expanded, a
 *   SubprocessSection rect wraps the band + body into one container.
 * - Flow: a lane-scoped box (in the chain's current lane), dashed like a
 *   note but in the subprocess accent so it never reads as a plain step.
 *   When expanded, a SubprocessRail brackets the body beneath it.
 */
export function SubprocessBand({
  row,
  variant,
  active = false,
  laneIndex,
  topPad,
  bottomPad,
  blockGeom,
  onClick,
}: {
  row: Row;
  variant: SwimlaneLayout["variant"];
  /** True when the active playback step is inside this collapsed subprocess. */
  active?: boolean;
  laneIndex: (id: string) => number;
  topPad: number;
  bottomPad: number;
  /** Pure geometry — box height + pre-wrapped label lines. */
  blockGeom?: SwimlaneBlockGeom;
  onClick: () => void;
}) {
  const sh = row.subprocessHeader;
  const expanded = sh?.expanded ?? false;
  const caret = expanded ? "▾" : "▸";

  if (variant === "flow") {
    // Lane-scoped box, like a note but in the subprocess accent.
    const toI = laneIndex(row.toLaneId);
    const col = toI < 0 ? 1 : toI + 1;
    return (
      <div
        data-subprocess-do={sh?.nodeId ?? "true"}
        data-subprocess-expanded={expanded ? "true" : "false"}
        data-row-y={row.y}
        onClick={onClick}
        className="group relative flex items-center justify-center cursor-pointer"
        style={{
          gridRow: row.y + 1,
          gridColumn: col,
          padding: `${topPad}px ${BLOCK_INSET}px ${bottomPad}px`,
          opacity: row.branchSelected === false ? 0.45 : 1,
        }}
      >
        <div
          className="w-full text-center flex items-center justify-center gap-1"
          style={{
            background: active ? "rgba(99,102,241,0.28)" : "rgba(99,102,241,0.10)",
            border: `1px dashed ${active ? "#a5b4fc" : SUBPROCESS_BORDER}`,
            borderRadius: 6,
            color: active ? "#e0e7ff" : "#c7d2fe",
            fontSize: 11,
            lineHeight: "14px",
            padding: "4px 8px",
            height: blockGeom?.rect.h,
            minHeight: blockGeom ? undefined : 22,
            boxSizing: "border-box",
          }}
        >
          <span style={{ color: "#818cf8", fontSize: 10, flexShrink: 0 }}>{caret}</span>
          <div>
            {(blockGeom?.lines.length ? blockGeom.lines : [sh?.label ?? "do"]).map((ln, li) => (
              <div key={li} style={{ whiteSpace: "nowrap" }}>{ln}</div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Sequence: full-width band.
  return (
    <div
      data-subprocess-do={sh?.nodeId ?? "true"}
      data-subprocess-expanded={expanded ? "true" : "false"}
      data-row-y={row.y}
      onClick={onClick}
      className="group cursor-pointer flex items-stretch"
      style={{
        gridRow: row.y + 1,
        gridColumn: "1 / -1",
        padding: `${topPad}px 16px ${bottomPad}px`,
        opacity: row.branchSelected === false ? 0.45 : 1,
      }}
    >
      <div
        className="w-full flex items-center gap-2"
        style={{
          background: "rgba(99,102,241,0.12)",
          border: `1px solid ${SUBPROCESS_BORDER}`,
          borderRadius: 6,
          color: "#c7d2fe",
          fontSize: 11,
          padding: "5px 12px",
        }}
      >
        <span
          style={{ color: "#818cf8", fontSize: 10, width: 10, flexShrink: 0 }}
          className="group-hover:text-indigo-300"
        >
          {caret}
        </span>
        <span style={{ fontWeight: 500 }}>{sh?.label ?? "do"}</span>
        <span style={{ flex: 1 }} />
        <span
          style={{ color: "#6366f1", fontSize: 9, letterSpacing: "0.05em", textTransform: "uppercase" }}
          className="group-hover:text-indigo-300"
        >
          {expanded ? "subprocess — click to collapse" : "subprocess — click to expand"}
        </span>
      </div>
    </div>
  );
}

function effectiveYEnd(f: Frame): number {
  return Math.max(f.yEnd, f.yStart - 1);
}

// Frame nesting depth + rect derivation live in scene's swimlane-geometry
// (`SwimlaneFrameGeom.depth` / `.rect`) — consumed via `frameGeomById`.

/**
 * Left bracket marking the extent of an expanded `do` in the flow
 * variant (which renders no frame rects). Spans the header row through
 * the last body row at the left edge of the subprocess's lane span.
 */
export function SubprocessRail({
  frame,
  rows,
  laneIndex,
  geom,
}: {
  frame: Frame;
  rows: readonly Row[];
  laneIndex: (id: string) => number;
  geom: RowGeometry;
}) {
  const yStart = frame.yStart;
  const yEnd = effectiveYEnd(frame);
  if (yEnd < yStart) return null;
  const top = LANE_HEADER_H + (geom.tops[yStart] ?? 0) + 4;
  let h = 0;
  for (let yy = yStart; yy <= yEnd; yy++) h += geom.heights[yy] ?? ROW_MIN_H;
  h -= 8;
  // Flow renders a block only on the CALLEE lane, so hug the leftmost
  // callee of the body. `frame.laneSpan` also unions caller lanes (which
  // flow draws nothing in) and the header's anchor lane — using it would
  // push the bracket left of any real block.
  let minLane = Number.POSITIVE_INFINITY;
  for (const r of rows) {
    if (!r.enclosingFrames.includes(frame.id)) continue;
    if (r.subprocessHeader?.nodeId === frame.id) continue; // skip own header anchor
    const li = laneIndex(r.toLaneId);
    if (li >= 0 && li < minLane) minLane = li;
  }
  const laneStart = minLane === Number.POSITIVE_INFINITY ? frame.laneSpan.start : minLane;
  const x = laneStart * LANE_WIDTH + 4;
  const cap = 6;
  return (
    <path
      data-subprocess-rail={frame.id}
      d={`M ${x + cap} ${top} L ${x} ${top} L ${x} ${top + h} L ${x + cap} ${top + h}`}
      fill="none"
      stroke={SUBPROCESS_BORDER}
      strokeWidth={1.5}
      strokeOpacity={0.7}
      strokeLinecap="round"
    />
  );
}

/**
 * Flow-only loop marker for an `each` frame: a violet dashed left bracket
 * spanning the iterated body plus a `↻ each …` badge. Flow draws no frame
 * rects, so without this an `each` loop is invisible (the body steps read as
 * a plain sequence). Hugs the leftmost callee lane of the body, like the
 * subprocess rail.
 */
export function EachLoopRail({
  frame,
  rows,
  laneIndex,
  geom,
  rowTopPad,
}: {
  frame: Frame;
  rows: readonly Row[];
  laneIndex: (id: string) => number;
  geom: RowGeometry;
  rowTopPad: (y: number) => number;
}) {
  const yStart = frame.yStart;
  const yEnd = effectiveYEnd(frame);
  if (yEnd < yStart) return null;
  const rowTop = geom.tops[yStart] ?? 0;
  // Start the bracket near the body's BLOCK top (row top + its top padding),
  // not the row top — that leaves the reserved head-room above for the badge
  // so the `↻ …` label isn't clipped at the top edge.
  const top = LANE_HEADER_H + rowTop + Math.max(rowTopPad(yStart) - 6, 4);
  let sumH = 0;
  for (let yy = yStart; yy <= yEnd; yy++) sumH += geom.heights[yy] ?? ROW_MIN_H;
  const bottom = LANE_HEADER_H + rowTop + sumH - 8;
  const h = Math.max(bottom - top, 10);
  let minLane = Number.POSITIVE_INFINITY;
  for (const r of rows) {
    if (!r.enclosingFrames.includes(frame.id)) continue;
    const li = laneIndex(r.toLaneId);
    if (li >= 0 && li < minLane) minLane = li;
  }
  const laneStart = minLane === Number.POSITIVE_INFINITY ? frame.laneSpan.start : minLane;
  const x = laneStart * LANE_WIDTH + 4;
  const cap = 6;
  const color = "#a78bfa"; // loop accent (violet), distinct from subprocess
  return (
    <g data-each-rail={frame.id}>
      {/* Drawn BOTTOM→TOP with an arrowhead at the top so the rail reads as the
          loop-back — the body re-enters at the head each iteration / retry
          attempt (PROCESS.md §4), not just a static bracket. */}
      <path
        d={`M ${x + cap} ${top + h} L ${x} ${top + h} L ${x} ${top} L ${x + cap} ${top}`}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeOpacity={0.85}
        strokeLinecap="round"
        strokeDasharray="4 3"
        markerEnd={svgFragmentUrl("sw-flow-tick")}
      />
      <text x={x + 3} y={top - 4} fontSize={9} fill={color}>↻ {frame.label}</text>
    </g>
  );
}

/**
 * Sequence-only section box for an expanded `do`: one full-width
 * container spanning the header band through the last body row, so the
 * subprocess reads as a single unit rather than a header plus a separate
 * body box. Nested `do`s inset via `depth`, so they nest visibly.
 */
export function SubprocessSection({
  frame,
  depth,
  nLanes,
  geom,
}: {
  frame: Frame;
  depth: number;
  nLanes: number;
  geom: RowGeometry;
}) {
  const yStart = frame.yStart;
  const yEnd = effectiveYEnd(frame);
  if (yEnd < yStart) return null;
  const insetX = depth * NESTED_INSET_X;
  const insetY = depth * NESTED_INSET_Y;
  const x = 8 + insetX;
  const w = nLanes * LANE_WIDTH - 16 - 2 * insetX;
  const top = LANE_HEADER_H + (geom.tops[yStart] ?? 0) + insetY + FRAME_GAP;
  let spanH = 0;
  for (let yy = yStart; yy <= yEnd; yy++) spanH += geom.heights[yy] ?? ROW_MIN_H;
  const h = Math.max(spanH - 2 * insetY - 2 * FRAME_GAP, 1);
  // Divider under the header row separates the title strip from the body.
  const headerBottom = top + (geom.heights[yStart] ?? ROW_MIN_H) - insetY - FRAME_GAP;
  return (
    <g data-subprocess-section={frame.id}>
      <rect
        x={x}
        y={top}
        width={w}
        height={h}
        rx={6}
        fill="rgba(99,102,241,0.05)"
        stroke={SUBPROCESS_BORDER}
        strokeWidth={1}
        strokeOpacity={0.7}
      />
      <line
        x1={x}
        y1={headerBottom}
        x2={x + w}
        y2={headerBottom}
        stroke={SUBPROCESS_BORDER}
        strokeWidth={1}
        strokeOpacity={0.4}
      />
    </g>
  );
}

function BranchTabs({
  frame,
  startX,
  y,
  maxX,
  onBranchSelect,
}: {
  frame: Frame;
  startX: number;
  y: number;
  maxX: number;
  onBranchSelect: (nodeId: NodeId, label: string) => void;
}) {
  let cursor = startX;
  const tabs: { branch: FrameBranch; x: number; w: number }[] = [];
  const skipLabel = frame.label.trim();
  for (const b of frame.branches) {
    if (b.label === skipLabel) continue;
    const text = b.label || "(default)";
    const w = Math.min(text.length * 6 + 14, maxX - cursor);
    if (w <= 12) break;
    tabs.push({ branch: b, x: cursor, w });
    cursor += w + FRAME_GAP;
    if (cursor >= maxX) break;
  }
  return (
    <g style={{ pointerEvents: "auto" }}>
      {tabs.map(({ branch, x, w }) => {
        const text = branch.label || "(default)";
        const empty = branch.yEnd < branch.yStart;
        const fill = branch.selected
          ? "rgba(56,189,248,0.18)"
          : "rgba(148,163,184,0.06)";
        const stroke = branch.selected ? "#0ea5e9" : "#475569";
        const label = branch.selected ? "#7dd3fc" : empty ? "#64748b" : "#cbd5e1";
        return (
          <g
            key={text}
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              // Clicking the already-selected branch unselects it
              // (walker falls back to its default branch). Optional
              // branches can therefore be toggled.
              onBranchSelect(frame.id, branch.selected ? "" : branch.label);
            }}
          >
            <rect
              x={x}
              y={y}
              width={w}
              height={14}
              rx={3}
              fill={fill}
              stroke={stroke}
              strokeWidth={branch.selected ? 1 : 0.6}
              strokeDasharray={branch.selected ? undefined : "3 3"}
            />
            <text
              x={x + 6}
              y={y + 11}
              fontSize={9}
              fill={label}
              fontWeight={branch.selected ? 600 : 400}
            >
              {text}
              {empty ? " ·" : ""}
            </text>
          </g>
        );
      })}
    </g>
  );
}

export function FrameGroup({
  frame,
  rect,
  pickedLabel,
  geom,
  onBranchSelect,
}: {
  frame: Frame;
  /** Solved frame rect from the pure geometry pass (depth insets and
   *  band-start top-extra already applied). */
  rect: { x: number; y: number; w: number; h: number };
  /** The frame's explicitly-picked branch label (exclusive frames only) —
   *  hidden sibling bands must not keep their divider/label over blank rows. */
  pickedLabel?: string;
  geom: RowGeometry;
  onBranchSelect: (nodeId: NodeId, label: string) => void;
}) {
  const { x, y, w, h } = rect;
  const labelGutter = 8 + frame.label.length * 6 + 8;
  // Merge threshold / run bound, annotated on the frame header: `parallel`'s
  // mergeAspect (`join 2`, `race`, boolean-over-names) or a `select N`
  // (switch) run bound. Same data attr as flow's gateway label, one contract.
  const threshold =
    frame.mergeAspect ??
    (frame.runCount !== undefined ? `≤${frame.runCount} of ${frame.branches.length}` : undefined);
  // Only EXCLUSIVE frames (if/try) expose the pick tabs — the walker ignores
  // branchSelection for switch/parallel, so a picker there is inert and
  // falsely signals exclusivity (frameAffordance.ts).
  const pickable = isFrameExclusive(frame.kind);
  // UML-alt band separators: every branch band after the frame's first row
  // gets a dashed divider at its top edge + its case label (labels suppressed
  // for parallel — auto `branch:N` noise). The FIRST band's label lives in
  // the title strip via BranchTabs / the frame label itself. A band hidden by
  // an explicit pick draws neither — its rows are gone (hiddenRows), and a
  // labeled divider over blank rows would advertise a phantom band.
  const dividers = frame.branches
    .filter((b) => b.yStart > frame.yStart && b.yEnd >= b.yStart)
    .filter((b) => pickedLabel === undefined || b.label === pickedLabel)
    .map((b) => {
      const rowTop = LANE_HEADER_H + (geom.tops[b.yStart] ?? 0);
      return { branch: b, lineY: rowTop + 2, labelY: rowTop + 12 };
    });
  return (
    <g data-frame-id={frame.id}>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={6}
        fill="rgba(56,189,248,0.04)"
        stroke="#334155"
      />
      {/* `do` frames carry their label in the full-width SubprocessBand
          header instead, so the rect stays a plain boundary box. */}
      {frame.kind !== "do" && (
        <text x={x + 8} y={y + 14} fontSize={11} fill="#94a3b8">
          {frame.label}
        </text>
      )}
      {threshold && (
        <text
          data-merge-threshold={threshold}
          x={x + w - 8}
          y={y + 14}
          fontSize={9}
          fontWeight={600}
          fill="#94a3b8"
          textAnchor="end"
        >
          {threshold}
        </text>
      )}
      {dividers.map(({ branch, lineY, labelY }) => (
        <g key={`band-${branch.label}-${branch.yStart}`}>
          <line
            data-band-divider={branch.label}
            x1={x}
            y1={lineY}
            x2={x + w}
            y2={lineY}
            stroke="#334155"
            strokeDasharray="5 4"
          />
          {showsBranchLabels(frame.kind) && (
            <text
              data-band-label={branch.label}
              x={x + 8}
              y={labelY}
              fontSize={9}
              fontStyle="italic"
              fontWeight={branch.selected ? 600 : 400}
              fill={branch.selected ? "#7dd3fc" : "#64748b"}
            >
              {branch.label || "(default)"}
            </text>
          )}
        </g>
      ))}
      {pickable && frame.branches.length > 0 && (
        <BranchTabs
          frame={frame}
          startX={x + labelGutter}
          y={y + 3}
          maxX={x + w - (threshold ? 10 + threshold.length * 6 : 6)}
          onBranchSelect={onBranchSelect}
        />
      )}
    </g>
  );
}
